<template>
  <div class="tracker-month-view">
    <div class="mt-4 flex justify-center">
      <t-select
        v-model="selectedMonthName"
        :options="months"
        @input="setSelectedMonth"
      />
      <span class="ten"></span>
      <t-select v-model="selectedYear" :options="years" />
    </div>

    <div class="calendar-month mt-6">
      <div class="weekdays">
        <div v-for="(weekday, w) in weekdays" :key="w">{{ weekday }}</div>
      </div>
      <div class="days" :id="`days-0`">
        <div
          v-for="(dayInPrevMonth, p) in daysInPrevMonth"
          :key="p + 32"
          class="prev-date"
        >
          {{ dayInPrevMonth }}
        </div>
        <div
          v-for="(dayInMonth, d) in daysInMonth"
          :key="d"
          class="flex flex-col relative mt-1"
          :class="{
            'today border rounded-lg border-primary': isToday(dayInMonth),
            selected: selectedDay === dayInMonth && sheetVisible
          }"
          @click="setAction(dayInMonth, d)"
        >
          <span class="absolute top-0 pt-1">{{ dayInMonth }}</span>

          <template v-if="logIsCompleteWithNotes(dayInMonth)">
            <i v-if="habitIsPositive" class="material-icons pt-2"
              >playlist_add_check</i
            >
            <negative-with-notes-icon v-else class="w-24px pt-2" />
          </template>

          <template v-else-if="logIsComplete(dayInMonth)">
            <i v-if="habitIsPositive" class="material-icons pt-2">done</i>
            <i v-else class="material-icons pt-2">clear</i>
          </template>

          <i v-else-if="logHasNotes(dayInMonth)" class="material-icons pt-2"
            >subject</i
          >
          <span
            v-else
            class="text-gray-400 font-semibold pt-2"
            :class="{ today: isToday(dayInMonth) }"
            >•</span
          >
        </div>
      </div>
    </div>

    <div
      v-if="logsComplete"
      class="w-full flex justify-center items-baseline mt-4"
    >
      <span class="text-xl">{{ logsComplete }}</span>
      <span
        class="text-xs ml-2"
        :class="habitIsPositive ? 'text-green-400' : 'text-warning'"
        >completed this month</span
      >
    </div>

    <mt-actionsheet
      :actions="actions"
      v-model="sheetVisible"
      cancelText="Cancel"
    >
    </mt-actionsheet>
  </div>
</template>

<script>
import moment from 'moment/src/moment'
import habitModule from '@/store/modules/habit.module'
import logModule from '@/store/modules/log.module'
import trackerModule from '@/store/modules/tracker.module'
import { habitsCollection, logsCollection } from '../firebase'
import { v4 as uuidv4 } from 'uuid'
import dateUtils from '@/assets/utils/date.utils'
import NegativeWithNotesIcon from '@/assets/icons/NegativeWithNotesIcon'

export default {
  name: 'TrackerMonthView',
  components: { NegativeWithNotesIcon },
  props: ['habit', 'days'],

  watch: {
    habit(habit) {
      this.years = trackerModule.initYears(habit, true)
      this.months = trackerModule.initMonths()
      const date = new Date(this.selectedYear, this.selectedMonth)
      this.generateDaysInMonth(date)
    }
  },

  data: () => ({
    actions: [
      { name: '', method: null },
      { name: '', method: null }
    ],
    selectedDay: '',
    sheetVisible: false,
    months: [],
    years: [],
    daysInMonth: [],
    daysInPrevMonth: [],
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  }),

  computed: {
    selectedHabit: habitModule.selectedHabit,
    selectedMonth: trackerModule.selectedMonth,
    selectedMonthName: trackerModule.selectedMonthName,
    selectedYear: trackerModule.selectedYear,
    logs: logModule.logs,
    logsComplete: logModule.logsComplete,

    habitIsPositive() {
      return this.selectedHabit.isPositive
    }
  },

  methods: {
    generateDaysInMonth(date) {
      logModule.getLogsForMonthYear(
        this.habit.id,
        this.selectedYear,
        this.selectedMonth
      )

      this.daysInPrevMonth = []
      this.daysInMonth = []

      const lastDay = new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0
      ).getDate()

      const prevLastDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        0
      ).getDate()

      let firstDayIndex = date.getDay() - 1
      if (firstDayIndex === -1) firstDayIndex = 6
      for (let x = firstDayIndex; x > 0; x--) {
        if (new Date(date.getFullYear(), date.getMonth(), 0).getDay() === 0)
          break
        this.daysInPrevMonth.push(prevLastDay - x + 1)
      }

      for (let i = 1; i <= lastDay; i++) {
        this.daysInMonth.push(i)
      }
    },

    setSelectedMonth(month) {
      const monthAsNumber = dateUtils.monthStringToNumber(month)
      if (this.selectedMonth === monthAsNumber) return

      this.selectedMonth = monthAsNumber
      const date = new Date(this.selectedYear, this.selectedMonth)
      this.generateDaysInMonth(date)
    },

    getLog(day) {
      return this.logs.find(
        log => log.habitId === this.habit.id && log.date === day
      )
    },

    getLogIndex(day) {
      return this.logs.findIndex(
        log => log.habitId === this.habit.id && log.date === day
      )
    },

    logIsCompleteWithNotes(day) {
      const log = this.getLog(day)
      if (!log) return false

      const logHasNotes = log.notes.length > 0
      const logIsComplete = log.isComplete

      return logHasNotes && logIsComplete
    },

    logIsComplete(day) {
      const log = this.getLog(day)
      if (!log) return false

      return log.isComplete
    },

    logHasNotes(day) {
      const log = this.getLog(day)
      if (!log) return false

      return log.notes.length > 0
    },

    setAction(day, index) {
      let log = this.getLog(day)

      let logId
      const year = this.selectedYear
      const month = this.selectedMonth
      const momentObj = moment()
        .year(year)
        .month(month)
        .date(day)
      const week = momentObj.week()
      const weekday = momentObj.day()
      if (!log) {
        log = {
          habitId: this.habit.id,
          isComplete: false,
          notes: '',
          date: day,
          month,
          week,
          weekday,
          year
        }
        logId = uuidv4()
      } else {
        logId = log.id
      }

      this.selectedDay = day
      this.sheetVisible = true

      const toggleResult = this.logIsComplete(day) ? 'Incomplete' : 'Complete'
      this.actions[0].name = 'Toggle to ' + toggleResult
      this.actions[0].method = () => {
        this.sheetVisible = false
        log.isComplete = !log.isComplete
        this.$store.dispatch('setLog', { logId, log })
        logModule.calculateLogsComplete()
      }

      const noteVerb = this.logHasNotes(day) ? 'Edit' : 'Add'
      const logIndex = this.getLogIndex(day)
      this.actions[1].name = noteVerb + ' Note'
      this.actions[1].method = () => {
        const formatMonth = this.isSingleDigit(month)
          ? '0' + (month + 1)
          : month + 1
        const formatDay = this.isSingleDigit(day) ? '0' + day : day
        const dayObject = {
          id: weekday,
          week,
          year,
          date: `${formatMonth}/${formatDay}/${year}`
        }
        this.$router.push({
          name: 'habit',
          params: { habit: this.habit, day: dayObject, log, logIndex }
        })
      }
    },

    isToday(day) {
      const today = moment().format('MM/DD/YYYY')
      const month = this.isSingleDigit(this.selectedMonth)
        ? '0' + (this.selectedMonth + 1)
        : this.selectedMonth + 1
      const date = this.isSingleDigit(day) ? '0' + day : day
      const monthDateYear = `${month}/${date}/${this.selectedYear}`
      return today === monthDateYear
    },

    isSingleDigit(num) {
      return num.toString().length === 1
    }
  },

  mounted() {
    if (this.selectedYear === 0) this.selectedYear = moment().year()

    this.years = trackerModule.initYears(this.habit, false)
    this.months = trackerModule.initMonths()
    const date = new Date(this.selectedYear, this.selectedMonth)
    this.generateDaysInMonth(date)
  }
}
</script>

<style lang="scss" scoped>
.tracker-month-view {
  padding-left: 10px;
  padding-right: 10px;
}
.ten {
  width: 10px;
}
.today {
  color: rgb(31, 116, 255);
}
.selected {
  background-color: rgb(255, 186, 0);
  border-color: rgb(255, 186, 0);
  border-radius: 0.5rem;
  span {
    color: white;
  }
  i {
    color: white;
  }
  svg {
    color: white;
  }
}
.weekdays {
  width: 100%;
  display: flex;
  align-items: center;

  div {
    font-size: 10px;
    font-weight: 400;
    width: calc(100% / 7);
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 10px;
  }
}
.days {
  width: 100%;
  display: flex;
  flex-wrap: wrap;

  div {
    font-size: 10px;
    width: calc(100% / 7);
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
.prev-date {
  color: transparent;
}
.text-success {
  color: rgb(104, 211, 145);
}
.text-warning {
  color: rgb(255, 186, 0);
}
.w-24px {
  width: 24px;
}
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='%23000000'><polygon points='0,0 100,0 50,50'/></svg>")
    no-repeat;
  background-size: 10px;
  background-position: right 0.5em top 60%;
  background-repeat: no-repeat;
  background-color: #ffffff;
}
</style>
